import { useState } from "react";
import ImgTag from "@/components/Helpers/ImgTag";
import PriceWithCurrency from "@/components/Helpers/PriceWithCurrency";

const banks = [
  { src: "/imgs/installment-bank/logo_bank_1.png", alt: "Al Rajhi Bank" },
  { src: "/imgs/installment-bank/logo_bank_2.png", alt: "Alinma" },
  { src: "/imgs/installment-bank/logo_bank_3.png", alt: "SAB" },
];

const plans = [3, 6, 12];

const steps = {
  en: [
    {
      title: 'Select "Bank Installment" at checkout',
      desc: 'On the payment page, choose "Bank Installment" and click confirm order',
    },
    {
      title: "Choose the bank",
      desc: "Select the bank you wish to use for installment payments",
    },
    {
      title: "Choose your installment plan and confirm",
      desc: "Select the number of months that works for you, then confirm your order",
    },
    {
      title: "Enter your bank card details",
      desc: "Enter your full card details, then click Submit",
    },
  ],
  ar: [
    {
      title: "اختر خيار التقسيط عبر البنك",
      desc: 'من صفحة الدفع، حدّد خيار "تقسيط عبر البنك" ثم اضغط تأكيد الطلب',
    },
    {
      title: "اختر البنك",
      desc: "اختر البنك الذي ترغب في التقسيط من خلاله",
    },
    {
      title: "اختر عدد الأقساط وأكّد الطلب",
      desc: "حدّد برنامج التقسيط المناسب لك ثم اضغط تأكيد",
    },
    {
      title: "أدخل بيانات بطاقتك البنكية",
      desc: "أدخل بيانات بطاقتك البنكية كاملة ثم اضغط ارسال",
    },
  ],
};

const tableHeaders = {
  en: ["Number of months", "Benefits", "Fees"],
  ar: ["عدد الشهور", "الفائدة", "رسوم المعالجة"],
};

const ApsInstallmentContent = ({ locale }) => {
  const [activeBank, setActiveBank] = useState(0);

  return (
    <div className={locale === "ar" ? "font-noto" : "font-poppins"}>
      {/* Title */}
      <h2 className="text-center text-primary md:text-[32px] font-[600] leading-[120%] mb-6">
        {locale === "ar" ? "ادفع بالتقسيط" : "Easy installment"}
      </h2>

      {/* Banks + Table Section */}
      <div className="flex flex-col md:flex-row items-start gap-8 mb-4 md:mb-8">
        {/* Bank Logos */}
        <div
          className={`flex ${
            locale === "en" ? "flex-row-reverse" : "flex-row"
          } md:flex-col gap-2 md:gap-4 w-full md:w-auto md:shrink-0 justify-center`}
        >
          {banks.map((bank, idx) => (
            <div
              key={bank.alt}
              onClick={() => setActiveBank(idx)}
              className={`rounded-md px-3 py-1 md:p-2 bg-[#FBFBFB] flex items-center justify-center md:flex-none md:w-[160px]  md:h-[60px] border-[1.5px] transition-colors cursor-pointer ${activeBank === idx ? "border-[#B3B3B3]" : "border-transparent hover:border-[#B3B3B3]"}`}
            >
              <ImgTag
                src={bank.src}
                alt={bank.alt}
                width={80}
                height={40}
                className="object-contain h-full w-[60px] md:w-[80px] max-h-[40px] md:max-h-[70px]"
              />
            </div>
          ))}
        </div>

        {/* Table */}
        <div
          className={`w-full overflow-x-auto ${
            locale === "ar" ? "md:pl-8" : "md:pr-8"
          }`}
        >
          <table
            className="w-full text-center table-fixed border-separate"
            style={{ borderSpacing: 0 }}
          >
            <thead>
              <tr className="bg-[#FBFBFB] text-[#6B7280]">
                {tableHeaders[locale].map((header) => (
                  <th
                    key={header}
                    className="py-1 md:py-2 px-1 font-normal text-[8px] md:text-[11px] w-1/3 border-e-[2px] border-[#F3F3F3] last:border-e-0 first:rounded-ss-lg last:rounded-se-lg"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {plans.map((month) => (
                <tr
                  key={month}
                  className="odd:bg-[#E9E9E9] even:bg-[#EFEFEF] text-[#6B7280] text-[8px] md:text-[14px] font-medium"
                >
                  <td className="py-1.5 md:py-2 px-4 w-1/3 border-e-[2px] border-[#F3F3F3]">
                    {month}
                  </td>
                  <td className="py-1.5 md:py-2 px-4 w-1/3 border-e-[2px] border-[#F3F3F3]">
                    <span className="inline-flex items-center gap-0.5">
                      0<span>%</span>
                    </span>
                  </td>
                  <td className="py-1.5 md:py-2 px-4 w-1/3">
                    <span className="inline-flex items-center gap-0.5">
                      <PriceWithCurrency
                        amount={0}
                        iconClassName="size-2.5"
                        className="gap-0.5"
                      />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Steps + Image Section */}
      <div className="flex flex-col md:flex-row gap-2">
        {/* Steps */}
        <div className="flex flex-col gap-4 md:gap-0 relative flex-1">
          {steps[locale].map((step, idx) => (
            <div key={idx}>
              <div className="flex items-start gap-4">
                {/* Number + Dotted Line */}
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-[26px] h-[26px] md:w-[42px] md:h-[42px] rounded-[8px] bg-[#00269A14] text-[#00269A] flex items-center justify-center z-10">
                    <span className="text-[16px] md:text-[24px] font-semibold font-sans">
                      {idx + 1}
                    </span>
                  </div>
                  {idx < steps[locale].length - 1 && (
                    <div
                      className="hidden md:block w-[1.5px] flex-1 min-h-[81px] mt-2 mb-2"
                      style={{
                        backgroundImage:
                          "repeating-linear-gradient(to bottom, #00269A 0 4px, transparent 4px 8px)",
                      }}
                    />
                  )}
                </div>

                {/* Text */}
                <div className="flex-1 pt-1">
                  <h3 className="font-[500] text-[12px] md:text-[20px] leading-[120%] text-black mb-1">
                    {step.title}
                  </h3>
                  <p className="font-normal text-[10px] md:text-[16px] leading-[130%] text-black">
                    {step.desc}
                  </p>
                </div>
              </div>
              {idx === 0 && (
                <div className="flex md:hidden items-start justify-center md:pt-2 pt-2">
                  <ImgTag
                    src={`/imgs/installment-bank/aps-installment-desc_${locale}.png`}
                    alt="Payment page"
                    width={300}
                    height={180}
                    className="rounded-lg object-contain w-[270px] h-auto md:w-[300px]"
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Payment Screenshot */}
        <div className="hidden md:flex items-start md:pt-2">
          <ImgTag
            src={`/imgs/installment-bank/aps-installment-desc_${locale}.png`}
            alt="Payment page"
            width={340}
            height={180}
            className="rounded-lg object-contain w-full h-auto md:w-[340px]"
          />
        </div>
      </div>
    </div>
  );
};

export default ApsInstallmentContent;
